import React, {useState, useEffect} from 'react';
import {useMediaQuery} from 'react-responsive';
import useLoanCalculator from '../hooks/useLoanCalculator';
import LoanForm from './LoanForm';
import SecciPopup from './SecciPopup';
import './LoanCalculator.css';
import WarningFooter from "./WarningFooter";
import {formatCurrency} from '../utils/currencyUtils';
import {renderRepresentativeExample} from '../utils/representativeExample';

const LoanCalculator = ({appConfig}) => {
    const {applyUrl, footerText, loginLabel, onlyApplyButton, applyButtonId, hideDuration, creditAccountLabel, fullDisclosureMode, representativeExampleTemplate, numDecimalMonthly} = appConfig;
    const [showPopup, setShowPopup] = useState(false);
    const [showError, setShowError] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);

    const isMobile = useMediaQuery({maxWidth: 767});

    const {
        loading,
        error,
        productData,
        amountSelected,
        durationSelected,
        currency,
        calculateInstallment,
        handleAmountChange,
        handleDurationChange,
        fetchSecciDocument,
        getValidDurations,
        amountTouched,
        durationTouched,
        apr
    } = useLoanCalculator(appConfig);

    useEffect(() => {
        if (!loading && !error) {
            const timer = setTimeout(() => {
                setFadeIn(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [loading, error]);

    useEffect(() => {
        if (error) {
            setShowError(true);
        }
    }, [error]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'calculator_interaction',
                cat_01: 1,
            });
        }

        if (window.posthog) {
            window.posthog.capture('calculator_interaction', {
                cat_01: 1,
            });
        }

        const formData = new FormData();
        formData.append('amount', amountSelected);
        formData.append('product', appConfig.productId);
        formData.append('offer', appConfig.productId);
        formData.append('duration', durationSelected * 30);

        // 1) forward everything from URL
        const params = new URLSearchParams(window.location.search);
        params.forEach((value, key) => {
            formData.append(key, value);
        });

        // 2) fill missing attribution from sourcebuster/referrer parser
        const attr = window.Attribution ? window.Attribution.collectAttribution() : {};
        Object.entries(attr).forEach(([key, value]) => {
            if (value === null || value === '') return;
            if (!formData.has(key)) formData.append(key, value);
        });

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = applyUrl;

        for (const [key, value] of formData.entries()) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
        }

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const handleSecciRequest = () => {
        setShowPopup(true);
    };

    const placeholderProductData = {
        amountRange: [5000, 10000],
        durationRange: ["1M", "12M"],
        defaultAmount: 5000,
        defaultDuration: "6M"
    };

    const installmentDetails = (!loading && !error && calculateInstallment())
        || {principal: amountSelected || 0, installmentAmount: 0};

    const isInactiveState = loading || error;

    const hasSelection = fullDisclosureMode ? (amountTouched && durationTouched) : true;
    const decimals = numDecimalMonthly ?? 0;
    const monthly = installmentDetails.installmentAmount || 0;
    const totalFromApi = installmentDetails.totalAmount;
    const total = hasSelection
        ? (totalFromApi != null ? totalFromApi : monthly * durationSelected)
        : 0;
    const effectiveApr = installmentDetails.apr != null ? installmentDetails.apr : apr;
    const aprc = installmentDetails.aprc != null ? installmentDetails.aprc : effectiveApr;
    const firstInstallment = installmentDetails.firstInstallment != null ? installmentDetails.firstInstallment : monthly;
    const lastInstallment = installmentDetails.lastInstallment != null ? installmentDetails.lastInstallment : monthly;
    const remainingCount = hasSelection && durationSelected > 0 ? durationSelected - 1 : 0;
    const originationFee = installmentDetails.loanOriginationFee != null ? installmentDetails.loanOriginationFee : 0;
    const repExampleHtml = fullDisclosureMode && representativeExampleTemplate
        ? renderRepresentativeExample(representativeExampleTemplate, {
              amount: hasSelection ? formatCurrency(installmentDetails.principal, 0, currency) : formatCurrency(0, 0, currency),
              duration: hasSelection ? durationSelected : 0,
              monthly: hasSelection ? formatCurrency(monthly, decimals) : formatCurrency(0, decimals),
              totalAmount: hasSelection ? formatCurrency(total, decimals) : formatCurrency(0, decimals),
              apr: effectiveApr !== null && effectiveApr !== undefined && effectiveApr !== '' ? effectiveApr : 0,
              aprc: aprc !== null && aprc !== undefined && aprc !== '' ? aprc : 0,
              currency: currency || '',
              firstInstallment: hasSelection ? formatCurrency(firstInstallment, decimals) : formatCurrency(0, decimals),
              lastInstallment: hasSelection ? formatCurrency(lastInstallment, decimals) : formatCurrency(0, decimals),
              remainingCount: remainingCount,
              remainingInstallment: hasSelection ? formatCurrency(monthly, decimals) : formatCurrency(0, decimals),
              loanOriginationFee: hasSelection ? formatCurrency(originationFee, decimals) : formatCurrency(0, decimals),
          })
        : '';

    const showFullDisclosureExample = fullDisclosureMode && repExampleHtml;
    const showFooterText = !showFullDisclosureExample && footerText;

    return (
        <div className={`loan-calculator ${fadeIn ? 'fade-in' : ''} ${fullDisclosureMode ? 'full-disclosure-mode' : ''}`}>
            {showError && (
                <div className="error-message">
                    {appConfig.apiErrorMessage || 'Something went wrong, try again later'}
                </div>
            )}

            <div className="calculator-container">
                <LoanForm
                    productData={isInactiveState ? placeholderProductData : productData}
                    amountSelected={amountSelected || 0}
                    durationSelected={durationSelected || 0}
                    currency={currency || '€'}
                    installmentDetails={installmentDetails}
                    handleAmountChange={handleAmountChange}
                    handleDurationChange={handleDurationChange}
                    onSubmit={handleSubmit}
                    onSecciRequest={handleSecciRequest}
                    getValidDurations={getValidDurations}
                    labels={appConfig}
                    isLoading={isInactiveState}
                    onlyApplyButton={onlyApplyButton}
                    hideDuration={hideDuration}
                    applyButtonId={applyButtonId}
                    creditAccountLabel={creditAccountLabel}
                    fullDisclosureMode={fullDisclosureMode}
                    amountTouched={amountTouched}
                    durationTouched={durationTouched}
                />
            </div>

            {loginLabel && (
                <div className="login-container">
                    <div dangerouslySetInnerHTML={{__html: loginLabel}}/>
                </div>
            )}
            {showFullDisclosureExample && (
                <div className="calculator-footer representative-example">
                    <div dangerouslySetInnerHTML={{__html: repExampleHtml}}/>
                </div>
            )}
            {showFooterText && (
                <div className="calculator-footer">
                    <div dangerouslySetInnerHTML={{__html: footerText}}/>
                </div>
            )}


            <SecciPopup
                isOpen={showPopup}
                onClose={() => setShowPopup(false)}
                fetchDocument={fetchSecciDocument}
            />
            {appConfig.warningContent && (
                <WarningFooter
                    title={appConfig.warningTitle || undefined}
                    content={appConfig.warningContent}
                />
            )}
        </div>
    );
};

export default LoanCalculator;
